/**
 * Text normalization and position finding utilities for highlights
 *
 * Pure functions for matching user-selected text to DOM content.
 * These handle differences between:
 * - User selection (includes newlines from visual breaks)
 * - DOM textContent (may have no whitespace between elements)
 * - Markdown source (includes ## headers, ** bold markers, etc.)
 */

/**
 * Normalize text for comparison by collapsing whitespace and removing markdown syntax
 * @param {string} text - Text to normalize
 * @returns {string} Normalized text
 */
export function normalizeText(text) {
  return text
    .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold markers (**text** -> text)
    .replace(/^#{1,6}\s+/gm, '') // Remove markdown headers (## Header -> Header) - multiline flag for selections spanning multiple lines
    .replace(/）\s+(\d+\.)/g, '）$1') // Remove space/newline between Chinese paren and numbered list: ）\n1. -> ）1.
    .replace(/。\s+(\d+\.)/g, '。$1') // Remove space/newline between period and numbered list: 。\n2. -> 。2.
    .replace(/\s*[-*+]\s+/g, ' ') // Remove markdown list markers (-, *, +) with surrounding spaces
    .replace(/\s*\d+\.\s+/g, ' ') // Remove numbered list markers (1. 2. 3. etc.) with surrounding spaces
    .replace(/\s+/g, ' ') // Collapse all whitespace (including newlines) to single spaces
    .replace(/\s*•/g, '•') // Remove ALL whitespace before bullet points (•) - MUST be after general whitespace collapse
    .trim();
}

/**
 * Aggressively normalize text by removing ALL whitespace
 * Used for fuzzy matching when normal normalization fails
 * @param {string} text - Text to normalize
 * @returns {string} Text with all whitespace removed
 */
export function normalizeTextAggressive(text) {
  return normalizeText(text).replace(/\s+/g, '');
}

/**
 * Extract text position in summary content
 * @param {string} summaryText - Full summary text
 * @param {string} selectedText - Selected text to find
 * @returns {Object|null} Position object with start and end, or null if not found
 */
export function findTextPosition(summaryText, selectedText) {
  if (!summaryText || !selectedText) {
    return null;
  }

  // Try exact match first
  let start = summaryText.indexOf(selectedText);
  if (start !== -1) {
    return {
      start,
      end: start + selectedText.length,
    };
  }

  // Try normalized match - extract the first few words to find the approximate location
  const normalizedSummary = normalizeText(summaryText);
  const normalizedSelected = normalizeText(selectedText);

  // Search for the normalized selected text in the normalized summary
  let normalizedMatchFound = normalizedSummary.indexOf(normalizedSelected) !== -1;

  // If normalized match fails, try aggressive normalization (removes ALL whitespace)
  // This handles cases where DOM textContent has no whitespace between elements
  // but user selection has newlines from visual line breaks
  if (!normalizedMatchFound) {
    const aggressiveSummary = normalizeTextAggressive(summaryText);
    const aggressiveSelected = normalizeTextAggressive(selectedText);
    normalizedMatchFound = aggressiveSummary.indexOf(aggressiveSelected) !== -1;
  }

  // Simple approach: find the actual position by searching for the text directly
  // Start with first 15 characters of the selected text (for Chinese, this is enough)
  const selectedStart = selectedText.trim().substring(0, 15);
  let actualStart = summaryText.indexOf(selectedStart);

  // If exact start not found, try normalized version
  if (actualStart === -1) {
    const normalizedStartText = normalizedSelected.substring(0, 15);
    actualStart = summaryText.indexOf(normalizedStartText);
  }

  if (actualStart !== -1) {
    // Found start, now find end
    const selectedEnd = selectedText.trim().slice(-15);
    const searchStartPos = actualStart + 10; // Start searching for end after at least 10 chars
    let actualEndMatch = summaryText.indexOf(selectedEnd, searchStartPos);

    // Try normalized end if exact end not found
    if (actualEndMatch === -1) {
      const normalizedEnd = normalizedSelected.slice(-15);
      actualEndMatch = summaryText.indexOf(normalizedEnd, searchStartPos);
    }

    if (actualEndMatch !== -1) {
      // Calculate actual end position
      const actualEnd =
        actualEndMatch +
        (actualEndMatch ===
        summaryText.indexOf(selectedText.trim().slice(-15), searchStartPos)
          ? selectedText.trim().slice(-15).length
          : normalizedSelected.slice(-15).length);
      return { start: actualStart, end: actualEnd };
    } else {
      // Fallback: use length calculation
      const actualEnd = actualStart + normalizedSelected.length;
      return { start: actualStart, end: actualEnd };
    }
  }

  // Fallback: search for word sequences
  const words = normalizedSelected.split(/\s+/).filter((w) => w.length > 1);
  const firstFewWords = words.slice(0, 3).join(' ');
  const lastFewWords = words.slice(-3).join(' ');

  let startPos = summaryText.indexOf(firstFewWords);

  // Try without spaces (critical for Chinese text)
  if (startPos === -1) {
    const firstFewWordsNoSpace = words.slice(0, 3).join('');
    startPos = summaryText.indexOf(firstFewWordsNoSpace);

    if (startPos !== -1) {
      const lastFewWordsNoSpace = words.slice(-3).join('');
      const endSearchStart = startPos + firstFewWordsNoSpace.length;
      const endPos = summaryText.indexOf(lastFewWordsNoSpace, endSearchStart);
      const finalEnd =
        endPos !== -1
          ? endPos + lastFewWordsNoSpace.length
          : startPos + normalizedSelected.replace(/\s+/g, '').length;
      return { start: startPos, end: finalEnd };
    }
  }

  // Last resort: fuzzy character matching
  if (startPos === -1) {
    const minSearchLen = Math.min(10, normalizedSelected.length);
    for (
      let len = Math.min(30, normalizedSelected.length);
      len >= minSearchLen;
      len--
    ) {
      const searchChunk = normalizedSelected.substring(0, len).replace(/\s+/g, '');
      let chunkPos = summaryText.indexOf(searchChunk);

      // If not found, try searching in aggressively normalized DOM text
      if (chunkPos === -1) {
        const aggressiveSummary = summaryText.replace(/\s+/g, '');
        const aggressiveChunkPos = aggressiveSummary.indexOf(searchChunk);
        if (aggressiveChunkPos !== -1) {
          // Map back to original position
          chunkPos = 0;
          let aggressiveIdx = 0;
          for (
            let i = 0;
            i < summaryText.length && aggressiveIdx < aggressiveChunkPos;
            i++
          ) {
            if (!/\s/.test(summaryText[i])) {
              aggressiveIdx++;
            }
            chunkPos = i + 1;
          }
        }
      }

      if (chunkPos !== -1) {
        const endChunk = normalizedSelected.slice(-len).replace(/\s+/g, '');
        let endPos = summaryText.indexOf(endChunk, chunkPos + searchChunk.length);

        // If not found, try in aggressive normalized text
        if (endPos === -1) {
          const aggressiveSummary = summaryText.replace(/\s+/g, '');
          const aggressiveEndPos = aggressiveSummary.indexOf(endChunk, chunkPos);
          if (aggressiveEndPos !== -1) {
            // Map back to original position
            let mappedEndPos = 0;
            let aggressiveIdx = 0;
            for (
              let i = 0;
              i < summaryText.length &&
              aggressiveIdx < aggressiveEndPos + endChunk.length;
              i++
            ) {
              if (!/\s/.test(summaryText[i])) {
                aggressiveIdx++;
              }
              mappedEndPos = i + 1;
            }
            endPos = mappedEndPos - endChunk.length;
          }
        }

        const finalEnd =
          endPos !== -1
            ? endPos + endChunk.length
            : chunkPos + normalizedSelected.replace(/\s+/g, '').length;
        return { start: chunkPos, end: finalEnd };
      }
    }
    return null;
  }

  const endSearchStart = startPos + firstFewWords.length;
  const endPos = summaryText.indexOf(lastFewWords, endSearchStart);
  const finalEnd =
    endPos !== -1 ? endPos + lastFewWords.length : startPos + normalizedSelected.length;

  return { start: startPos, end: finalEnd };
}
